/*
 * Breadcrumb menu tracker
 *
 * On menu click: stores the menu path (handles of ancestors) in sessionStorage
 * and injects an `_mp` URL param into the link href so the path survives
 * Ctrl/Cmd+Click into a new tab.
 *
 * On collection / product page load (and pjax:complete): if the stored path's
 * leaf matches the current collection, replaces the breadcrumb DOM with the
 * full menu path (visible only — server-rendered JSON-LD stays as fallback).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'breadcrumbMenuPath';
  var URL_PARAM = '_mp';
  var COLLECTION_HANDLE_RE = /\/collections\/([^/?#]+)/;

  function extractCollectionHandle(href) {
    if (!href) return null;
    var match = String(href).match(COLLECTION_HANDLE_RE);
    return match ? match[1] : null;
  }

  function getCleanUrl(href) {
    if (!href) return href;
    try {
      var url = new URL(href, window.location.origin);
      url.searchParams.delete(URL_PARAM);
      var search = url.searchParams.toString();
      return url.pathname + (search ? '?' + search : '') + (url.hash || '');
    } catch (e) {
      return String(href).split('?')[0];
    }
  }

  function getMenuRoot(el) {
    return el.closest ? el.closest('[data-breadcrumb-menu]') : null;
  }

  function findDirectAnchor(li) {
    var direct = li.querySelector(':scope > a');
    if (direct) return direct;
    var children = li.children;
    for (var i = 0; i < children.length; i++) {
      var c = children[i];
      var tag = c.tagName;
      if (tag === 'UL' || tag === 'OL') continue;
      var inner = c.querySelector(':scope > a');
      if (inner) return inner;
    }
    return null;
  }

  function dedupeConsecutive(arr) {
    var result = [];
    for (var i = 0; i < arr.length; i++) {
      if (arr[i] !== result[result.length - 1]) result.push(arr[i]);
    }
    return result;
  }

  function buildPathFromAnchor(anchor) {
    var root = getMenuRoot(anchor);
    if (!root) return null;

    var clickedHandle = extractCollectionHandle(anchor.getAttribute('href'));
    if (!clickedHandle) return null;

    var path = [clickedHandle];
    var li = anchor.closest('li');

    while (li) {
      var parentUl = li.parentElement;
      if (!parentUl) break;
      var parentLi = parentUl.closest('li');
      if (!parentLi || !root.contains(parentLi)) break;

      var ancestorAnchor = findDirectAnchor(parentLi);
      if (ancestorAnchor) {
        var handle = extractCollectionHandle(ancestorAnchor.getAttribute('href'));
        if (handle) path.unshift(handle);
      }
      li = parentLi;
    }

    return {
      menu: root.getAttribute('data-breadcrumb-menu'),
      path: dedupeConsecutive(path),
    };
  }

  function saveMenuPath(data) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  function parseUrlParam(value) {
    if (!value) return null;
    var colonIdx = value.indexOf(':');
    if (colonIdx === -1) return null;
    var menu = value.slice(0, colonIdx);
    var path = value.slice(colonIdx + 1).split('/').filter(Boolean);
    if (!menu || !path.length) return null;
    return { menu: menu, path: dedupeConsecutive(path) };
  }

  function loadMenuPath() {
    try {
      var url = new URL(window.location.href);
      var fromUrl = parseUrlParam(url.searchParams.get(URL_PARAM));
      if (fromUrl) return fromUrl;
    } catch (e) {}
    try {
      var stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return null;
  }

  function encodeUrlParamValue(data) {
    return data.menu + ':' + data.path.join('/');
  }

  function handleMenuMousedown(e) {
    var anchor = e.target && e.target.closest && e.target.closest('a[href*="/collections/"]');
    if (!anchor || !getMenuRoot(anchor)) return;

    var data = buildPathFromAnchor(anchor);
    if (!data) return;
    saveMenuPath(data);

    var href = anchor.getAttribute('href');
    if (!href || href.charAt(0) === '#' || /^javascript:/i.test(href)) return;
    try {
      var url = new URL(href, window.location.origin);
      url.searchParams.set(URL_PARAM, encodeUrlParamValue(data));
      anchor.setAttribute(
        'href',
        url.pathname + (url.search || '') + (url.hash || '')
      );
    } catch (err) {}
  }

  function findMenuItemByHandle(menuRoot, handle) {
    var anchors = menuRoot.querySelectorAll('a[href*="/collections/"]');
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      if (extractCollectionHandle(a.getAttribute('href')) === handle) {
        var span = a.querySelector('span');
        var title = (span ? span.textContent : a.textContent).trim();
        return { url: getCleanUrl(a.getAttribute('href')), title: title };
      }
    }
    return null;
  }

  function makeListItem(url, title, position) {
    var li = document.createElement('li');
    li.setAttribute('itemprop', 'itemListElement');
    li.setAttribute('itemscope', '');
    li.setAttribute('itemtype', 'https://schema.org/ListItem');

    var a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('itemprop', 'item');

    var span = document.createElement('span');
    span.setAttribute('itemprop', 'name');
    span.textContent = title;
    a.appendChild(span);

    var meta = document.createElement('meta');
    meta.setAttribute('itemprop', 'position');
    meta.setAttribute('content', String(position));
    a.appendChild(meta);

    li.appendChild(a);
    return li;
  }

  function overrideBreadcrumb() {
    var host = document.getElementById('breadcrumb-host');
    if (!host || host.getAttribute('data-breadcrumb-overridden') === 'true') return;

    var template = host.getAttribute('data-template');
    if (template !== 'collection' && template !== 'product') return;

    var currentHandle = host.getAttribute('data-collection-handle');
    if (!currentHandle) return;

    var data = loadMenuPath();
    if (!data || !data.path || !data.path.length) return;
    if (data.path[data.path.length - 1] !== currentHandle) return;

    var menuRoot = document.querySelector(
      '[data-breadcrumb-menu="' + data.menu + '"]'
    );
    if (!menuRoot) return;

    var items = [].slice.call(host.children);
    if (!items.length) return;

    var homeIdx = 0;
    var activeIdx = -1;
    for (var j = 0; j < items.length; j++) {
      if (items[j].classList && items[j].classList.contains('active')) {
        activeIdx = j;
        break;
      }
    }
    if (activeIdx <= homeIdx) return;

    var activeTitle = items[activeIdx].textContent.trim().toLowerCase();

    var ancestors = [];
    for (var i = 0; i < data.path.length - 1; i++) {
      if (data.path[i] === currentHandle) continue;
      var item = findMenuItemByHandle(menuRoot, data.path[i]);
      if (!item) return;
      if (item.title.trim().toLowerCase().indexOf(activeTitle) !== -1) continue;
      ancestors.push(item);
    }

    var separatorTpl = null;
    for (var k = 0; k < items.length; k++) {
      if (items[k].classList && items[k].classList.contains('svg-element-ui')) {
        separatorTpl = items[k];
        break;
      }
    }
    if (!separatorTpl) return;

    for (var m = homeIdx + 1; m < activeIdx; m++) {
      items[m].remove();
    }

    var fragment = document.createDocumentFragment();
    var position = 2;

    function addLevel(url, title) {
      fragment.appendChild(separatorTpl.cloneNode(true));
      fragment.appendChild(makeListItem(url, title, position));
      position++;
    }

    for (var n = 0; n < ancestors.length; n++) {
      addLevel(ancestors[n].url, ancestors[n].title);
    }

    if (template === 'product') {
      var collectionItem = findMenuItemByHandle(menuRoot, currentHandle);
      if (collectionItem) addLevel(collectionItem.url, collectionItem.title);
    }

    fragment.appendChild(separatorTpl.cloneNode(true));

    items[homeIdx].after(fragment);

    var activeMeta = items[activeIdx].querySelector('meta[itemprop="position"]');
    if (activeMeta) activeMeta.setAttribute('content', String(position));

    host.setAttribute('data-breadcrumb-overridden', 'true');
  }

  document.addEventListener('mousedown', handleMenuMousedown, true);

  // NOTE: overrideBreadcrumb is currently disabled. Server-side breadcrumb is
  // authoritative. Re-enable later if multi-branch menu support is needed and
  // we trust the DOM mutation logic.
  // if (document.readyState === 'loading') {
  //   document.addEventListener('DOMContentLoaded', overrideBreadcrumb);
  // } else {
  //   overrideBreadcrumb();
  // }
  // document.addEventListener('pjax:complete', overrideBreadcrumb);
})();
